import { useTrackEvent } from "@Data-Protocol/event-tracker"
import ReactPlayer from "react-player"
import {
  aIsMobileMenuOpen,
  aIsVolumeSliderVisible,
  useAtomValue,
  useSetAtom,
} from "~/atoms"
import { useIsFullscreenEnabled } from "~/hooks"
import useWindowSize from "~/hooks/useWindowSize"
import { cn } from "~/utils"
import CaptionControl from "./captions"
import CurrentTime from "./current-time"
import FullscreenControl from "./fullscreen"
import MenuButton from "./menu-button"
import MuteControl from "./mute"
import PlayControl from "./play"
import ProgressSlider from "./progress-slider"
import QualityControl from "./quality"
import SpeedControl from "./speed"
export default function ControlBar({
  videoRef,
  className,
  logoRedirectUrl,
}: {
  videoRef: React.RefObject<ReactPlayer>
  className?: string
  logoRedirectUrl?: string
}) {
  const isFullscreenEnabled = useIsFullscreenEnabled()
  const isMobileMenuOpen = useAtomValue(aIsMobileMenuOpen)
  const setIsVolumeSliderVisible = useSetAtom(aIsVolumeSliderVisible)
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 640
  const { trackAction: trackLogo } = useTrackEvent({
    name: "logo",
    type: "click",
    action: "open",
  })

  const onLogoClick = () => {
    trackLogo({
      action_meta: {
        location: logoRedirectUrl,
      },
    })
  }

  const logo = (
    <>
      <img
        src="/dp-icon-white.svg"
        alt="Logo"
        className="block h-4 pr-1 sm:hidden"
      />
      <img
        src="/data-protocol-control.svg"
        alt="Logo"
        className="hidden sm:block"
      />
    </>
  )

  return (
    <div
      className={cn(
        "pointer-events-none touch-none select-none pb-0 pt-0 sm:pt-24",
        className
      )}
      onMouseLeave={() => setIsVolumeSliderVisible(false)}
    >
      {!isMobile ? (
        <div className="pointer-events-auto relative touch-auto">
          <div className="px-1 sm:px-4">
            <ProgressSlider className="" videoRef={videoRef} />
          </div>

          <div className="flex items-center justify-between gap-1 sm:gap-4">
            <div className="flex w-full items-center">
              <PlayControl className="pl-2 sm:pl-5" />
              <MuteControl />
              <CurrentTime />
              <div className="flex flex-1 justify-end">
                {logoRedirectUrl ? (
                  <a
                    href={logoRedirectUrl}
                    onClick={onLogoClick}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {logo}
                  </a>
                ) : (
                  <>{logo}</>
                )}
              </div>
            </div>
            <div className="flex">
              <CaptionControl />
              <QualityControl videoRef={videoRef} />
              <SpeedControl
                className={cn(!isFullscreenEnabled && "pr-2 sm:pr-5")}
              />
              {isFullscreenEnabled && (
                <FullscreenControl className="pr-2 sm:pr-5" />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="pointer-events-auto relative touch-auto bg-black/70 p-2">
          <div
            // @ts-expect-error inert is not in React's types yet
            inert={!isMobileMenuOpen ? "" : undefined}
            className={cn(
              "mobile-menu absolute bottom-0 left-0 right-0 top-px z-10 flex items-center gap-1 p-2 transition-all duration-200",
              isMobileMenuOpen
                ? "translate-x-0 opacity-100"
                : "pointer-events-none translate-x-full opacity-0"
            )}
          >
            <div className="flex-1" />
            <MuteControl />
            <CaptionControl />
            <QualityControl videoRef={videoRef} />
            <SpeedControl className={cn(!isFullscreenEnabled && "")} />
            {isFullscreenEnabled && (
              <FullscreenControl className="pr-2 sm:pr-5" />
            )}
            <MenuButton className="pr-2" />
          </div>

          <div
            // @ts-expect-error inert is not in React's types yet
            inert={isMobileMenuOpen ? "" : undefined}
            className={cn(
              "mobile-menu transition-opacity duration-200",
              isMobileMenuOpen ? "opacity-0" : "opacity-100"
            )}
          >
            <div className="mt-px flex w-full items-center">
              <CurrentTime />
              <div className={cn("flex-1 px-5")}>
                <ProgressSlider className="pb-0" videoRef={videoRef} />
              </div>
              <div className="">
                {logoRedirectUrl ? (
                  <a
                    href={logoRedirectUrl}
                    onClick={onLogoClick}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {logo}
                  </a>
                ) : (
                  <>{logo}</>
                )}
              </div>
              <MenuButton className="pr-2" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
